import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Form,
  Grid,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from "antd";
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DollarOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { FIRESTORE_CARTS_TABLE } from "../../../../constants/FirestoreConstant";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import {
  updateFirebaseClientIsAuthorized,
  updateFirebaseClientType,
  updateUser,
} from "../../../../functions/firestore/users";
import styled from "styled-components";
import _ from "lodash";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import { getCustomersExcelExportData } from "../../../../functions/excel";
import * as dayjs from "dayjs";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { isCountryInEuropeanUnion, validateVatNumber } from "../../../../functions/validator";
import countryData from "../../../../data/countries/countries.json";
import relativeTime from "dayjs/plugin/relativeTime";
import ExcelExport from "../../../../components/shared-components/ExcelExport";
import OrderDrawer from "../../../../components/shared-components/Orders/OrderDrawer";
import CustomerDrawer from "../../../../components/shared-components/Customers/CustomerDrawer";
import { colors } from "../../../../utils/theme";
import { useCrmNotes } from "../../../../data/hooks/useCrmNotes";
import { useScopedCustomers } from "../../../../data/hooks/useScopedCustomers";
import { useCmsUsers } from "../../../../data/hooks/useCmsUsers";
import { usePermissions } from "../../../../data/hooks/usePermissions";
import { PERMISSIONS, SCOPE_COUNTRIES, SCOPE_CUSTOMERS, SCOPE_NONE } from "../../../../functions/permissions";

dayjs.extend(relativeTime);

const { Option } = Select;

const Customers = () => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firestore = useFirestore();
  // Klanten beperkt tot de landen van de ingelogde gebruiker (BMX-35).
  const allUsers = useScopedCustomers();
  const cmsUsers = useCmsUsers();
  // Deze pagina leest de collectie, dus abonneert de pagina zelf. Stond tot nu
  // toe in layouts/app-layout (op elke CMS-pagina) -- zie docs/cms-performance.md.
  useFirestoreConnect([{ collection: FIRESTORE_CARTS_TABLE }]);
  const carts = useSelector((state) => state.firestoreReducer.ordered.carts);
  const { can, scope } = usePermissions();
  const canManageCustomers = can(PERMISSIONS.CUSTOMERS_MANAGE);
  const [editForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const salesPersonUsers = _.unionBy(
    _.filter(allUsers, (user) => user.isSalesPerson),
    cmsUsers,
    "id",
  );
  const [customerList, setCustomerList] = useState(null);
  const [filteredList, setFilteredList] = useState(null);
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isSalesPerson, setIsSalesPerson] = useState(false);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [customerCountries, setCustomerCountries] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [invoiceDiffers, setInvoiceDiffers] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [mobileFilter, setMobileFilter] = useState("all");
  const [displayCount, setDisplayCount] = useState(20);
  const [hasCatalog, setHasCatalog] = useState(false);
  const [hasPromotionalMaterial, setHasPromotionalMaterial] = useState(false);

  // Use CRM notes hook to fetch latest contact per user
  const {
    latestNotesMap,
    fetchLatestNotesForUsers,
    isLoading: notesLoading,
  } = useCrmNotes({
    autoFetch: false,
    fetchLatestPerUser: true,
  });

  useEffect(() => {
    if (!!allUsers) {
      const customers = allUsers.filter((item) => !!item.authorization && item.authorization.isReviewed);
      const sortedCustomers = _.orderBy(
        customers,
        (item) => {
          return !!item.created_at && item.created_at;
        },
        ["desc"],
      );
      setCustomerList(sortedCustomers);

      // Apply current filter
      const searchResults = searchText ? utils.wildCardSearchOnCustomer(sortedCustomers, searchText) : sortedCustomers;
      const filtered = applyMobileFilter(searchResults, mobileFilter);
      setFilteredList(filtered);

      // Get all unique countries
      let countries = [];
      customers.map((item) => {
        if (!!item.company && !!item.company.country && !countries.includes(item.company.country)) {
          countries.push(item.company.country);
        }
      });
      setCustomerCountries(countries);

      // Fetch last contact (latest crmNotes) for each customer using the hook
      const userIds = customers.map((c) => c.id);
      if (userIds.length > 0) {
        fetchLatestNotesForUsers(userIds);
      }
    }
  }, [allUsers, carts, mobileFilter, searchText, fetchLatestNotesForUsers]);

  const showDrawerUser = (user) => {
    setDrawerUser(user);
    setDrawerVisible(true);
  };

  const userTypeMenu = (client) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => updateFirebaseClientType(client, false)}>{t("client")}</a>
      </Menu.Item>
      <Menu.Item>
        <a onClick={() => updateFirebaseClientType(client, true)}>{t("sales_person")}</a>
      </Menu.Item>
    </Menu>
  );

  const userAuthorizatedMenu = (client) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => updateFirebaseClientIsAuthorized(client, true)}>{t("access.key")}</a>
      </Menu.Item>
      <Menu.Item>
        <a onClick={() => updateFirebaseClientIsAuthorized(client, false)}>{t("no_access")}</a>
      </Menu.Item>
    </Menu>
  );

  const showOrderDrawer = (order) => {
    setCurrentOrder(order);
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key="1">
        <ExcelExport
          data={
            customerCountries?.length > 0
              ? customerCountries.map((item, index) => getCustomersExcelExportData(customerList, item))
              : []
          }
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " Klanten " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("excel")}
        </ExcelExport>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: t("company"),
      dataIndex: "company",
      render: (_, user) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            showDrawerUser(user);
          }}
        >
          {user?.company?.name}
        </a>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
    },
    {
      title: t("user_type"),
      render: (_, data) => {
        const tag = (
          <Tag color={data.isSalesPerson ? "lime" : "pink"}>{data.isSalesPerson ? t("sales_person") : t("client")}</Tag>
        );
        if (!canManageCustomers) return tag;
        return (
          <Dropdown overlay={userTypeMenu(data)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>{tag}</a>
          </Dropdown>
        );
      },
    },
    {
      title: t("status"),
      render: (_, data) => {
        const tag = (
          <Tag color={data.authorization.isAuthorized ? "geekblue" : "red"}>
            {data.authorization.isAuthorized ? t("access.key") : t("no_access")}
          </Tag>
        );
        if (!canManageCustomers) return tag;
        return (
          <Dropdown overlay={userAuthorizatedMenu(data)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>{tag}</a>
          </Dropdown>
        );
      },
    },
    {
      title: t("last_contact"),
      dataIndex: "id",
      render: (userId) => {
        const lastContact = latestNotesMap[userId];
        return <span>{!!lastContact ? dayjs.unix(lastContact).fromNow() : "-"}</span>;
      },
      sorter: (a, b) => {
        const aTimestamp = latestNotesMap[a.id] || 0;
        const bTimestamp = latestNotesMap[b.id] || 0;
        return aTimestamp - bTimestamp;
      },
    },
    {
      title: t("last_order"),
      dataIndex: "lastOrderTimestamp",
      render: (data) => <span>{!!data ? dayjs.unix(data).fromNow() : "-"}</span>,
    },
    {
      title: t("last_login_timestamp"),
      dataIndex: "lastLoginTimestamp",
      render: (data) => <span>{!!data ? dayjs.unix(data).fromNow() : "-"}</span>,
      sorter: (a, b) => {
        const aTimestamp = a.lastLoginTimestamp || 0;
        const bTimestamp = b.lastLoginTimestamp || 0;
        return aTimestamp - bTimestamp;
      },
    },
    {
      title: t("items_in_cart"),
      dataIndex: "id",
      render: (uid) => {
        const cart = carts?.find((cart) => cart.id === uid);
        const cartItemCount = cart?.products?.length || 0;
        const cartTotal = cart?.products?.reduce((sum, product) => sum + product.price * product.quantity, 0) || 0;

        if (cartItemCount > 0)
          return (
            <span>
              {cartItemCount} ({CURRENCY} {formatPrice(cartTotal)})
            </span>
          );
        else return 0;
      },
    },
    {
      title: t("marketing_materials"),
      dataIndex: "id",
      render: (_, user) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Checkbox
            checked={user.hasCatalog || false}
            disabled={!canManageCustomers}
            onChange={(e) => {
              updateUser(user.id, { hasCatalog: e.target.checked })
                .then(() => messageApi.success(t("saved")))
                .catch((err) => messageApi.error(err));
            }}
          >
            {t("catalog")}
          </Checkbox>
          <Checkbox
            checked={user.hasPromotionalMaterial || false}
            disabled={!canManageCustomers}
            onChange={(e) => {
              updateUser(user.id, { hasPromotionalMaterial: e.target.checked })
                .then(() => messageApi.success(t("saved")))
                .catch((err) => messageApi.error(err));
            }}
          >
            {t("promotional_material")}
          </Checkbox>
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown
            menu={
              <Menu>
                {canManageCustomers && (
                  <Menu.Item onClick={() => openEditModal(elm)}>
                    <Flex alignItems="center">
                      <EditOutlined />
                      <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                    </Flex>
                  </Menu.Item>
                )}
                <Menu.Item
                  onClick={() => {
                    showDrawerUser(elm);
                  }}
                >
                  <Flex alignItems="center">
                    <EyeOutlined />
                    <span style={{ marginLeft: 4 }}>{t("view")}</span>
                  </Flex>
                </Menu.Item>
              </Menu>
            }
          />
        </div>
      ),
    },
  ];

  const cartColumns = [
    {
      title: t("name"),
      render: (_, data) => (
        <div className="d-flex">
          <span>{`${data.name} (${data.code})`}</span>
        </div>
      ),
    },
    {
      title: t("price"),
      dataIndex: "price",
      render: (data) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(data)}</span>,
      align: "center",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      render: (data) => <span>{data}</span>,
      align: "center",
    },
    {
      title: t("sub_total"),
      dataIndex: "paymentPrice",
      render: (_, data) => (
        <span style={{ whiteSpace: "pre-line" }}>{CURRENCY + formatPrice(data.price * data.quantity)}</span>
      ),
      align: "right",
    },
  ];

  const saveUser = () => {
    editForm.validateFields().then((values) => {
      setSubmitLoading(true);
      const deliveryAddress = {
        street: values.street,
        houseNumber: values.houseNumber,
        addition: values.addition || null,
        zipCode: values.zipCode,
        city: values.city,
      };
      const invoiceAddress = invoiceDiffers
        ? {
            street: values.invoiceStreet,
            houseNumber: values.invoiceHouseNumber,
            addition: values.invoiceAddition || null,
            zipCode: values.invoiceZipCode,
            city: values.invoiceCity,
          }
        : null;

      updateUser(editUser.id, {
        salesPerson: !isSalesPerson ? selectedSalesPerson : null,
        isSalesPerson: isSalesPerson,
        hasCatalog: hasCatalog,
        hasPromotionalMaterial: hasPromotionalMaterial,
        "address.delivery": deliveryAddress,
        "address.invoice": invoiceAddress,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phoneNumber: values.phone,
        company: {
          name: values.companyName.trim(),
          vatNumber: values.vatNumber?.trim() || null,
          country: values.country.trim(),
        },
        companyName: values.companyName.trim(),
      })
        .then(() => {
          closeEditModal();
        })
        .catch((err) => {
          console.log(err);
          setSubmitLoading(false);
          messageApi.error(err);
        });
    });
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setIsSalesPerson(user.isSalesPerson || false);
    setHasCatalog(user.hasCatalog || false);
    setHasPromotionalMaterial(user.hasPromotionalMaterial || false);
    if (!!user.salesPerson) setSelectedSalesPerson(user.salesPerson);
    const deliveryAddress = user.address.delivery;
    const invoiceAddress = user.address.invoice;
    editForm.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phoneNumber,
      companyName: user.company.name,
      vatNumber: user.company.vatNumber,
      country: user.company.country,
      addition: deliveryAddress.addition,
      city: deliveryAddress.city,
      houseNumber: deliveryAddress.houseNumber,
      street: deliveryAddress.street,
      zipCode: deliveryAddress.zipCode,
    });
    // Invoice address
    if (!!invoiceAddress) {
      setInvoiceDiffers(true);
      editForm.setFieldsValue({
        invoiceAddition: invoiceAddress.addition,
        invoiceCity: invoiceAddress.city,
        invoiceHouseNumber: invoiceAddress.houseNumber,
        invoiceStreet: invoiceAddress.street,
        invoiceZipCode: invoiceAddress.zipCode,
      });
    }
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditUser(null);
    setEditModalVisible(false);
    setIsSalesPerson(false);
    setSelectedSalesPerson(null);
    setHasCatalog(false);
    setHasPromotionalMaterial(false);
    editForm.resetFields();
    setInvoiceDiffers(false);
    setSubmitLoading(false);
  };

  const onSearch = (value, customers) => {
    setSearchText(value);
    setDisplayCount(20);
    const searchResults = utils.wildCardSearchOnCustomer(customers ? customers : customerList, value);
    const filtered = applyMobileFilter(searchResults, mobileFilter);
    setFilteredList(filtered);
  };

  const getCartTotal = (userId) => {
    const cart = carts?.find((c) => c.id === userId);
    if (!cart || !cart.products) return 0;
    return cart.products.reduce((sum, product) => sum + product.price * product.quantity, 0);
  };

  const applyMobileFilter = (customers, filter) => {
    if (!customers) return customers;
    const now = dayjs();
    const twentyFourHoursAgo = now.subtract(48, "hour");

    switch (filter) {
      case "activelyOrdering":
        return customers
          .filter((user) => {
            const cart = carts?.find((c) => c.id === user.id);
            const hasCartValue = cart && cart.products && cart.products.length > 0;
            const lastLogin = user.lastLoginTimestamp ? dayjs.unix(user.lastLoginTimestamp) : null;
            const isRecentlyActive = lastLogin && lastLogin.isAfter(twentyFourHoursAgo);
            return hasCartValue && isRecentlyActive;
          })
          .sort((a, b) => (b.lastLoginTimestamp || 0) - (a.lastLoginTimestamp || 0));
      case "missedOpportunity":
        return customers
          .filter((user) => {
            const cart = carts?.find((c) => c.id === user.id);
            const hasCartValue = cart && cart.products && cart.products.length > 0;
            const lastLogin = user.lastLoginTimestamp ? dayjs.unix(user.lastLoginTimestamp) : null;
            const isNotRecentlyActive = !lastLogin || lastLogin.isBefore(twentyFourHoursAgo);
            return hasCartValue && isNotRecentlyActive;
          })
          .sort((a, b) => (b.lastLoginTimestamp || 0) - (a.lastLoginTimestamp || 0));
      case "cartValue":
        return [...customers]
          .filter((user) => {
            const cart = carts?.find((c) => c.id === user.id);
            return cart && cart.products && cart.products.length > 0;
          })
          .sort((a, b) => getCartTotal(b.id) - getCartTotal(a.id));
      case "all":
      default:
        return [...customers].sort((a, b) => (b.lastLoginTimestamp || 0) - (a.lastLoginTimestamp || 0));
    }
  };

  const getFilterDescription = () => {
    switch (mobileFilter) {
      case "activelyOrdering":
        return "Klanten die in de afgelopen 48 uur actief waren en producten in hun winkelwagen hebben";
      case "missedOpportunity":
        return "Klanten met producten in hun winkelwagen maar langer dan 48 uur niet actief";
      case "cartValue":
        return "Klanten met winkelwagen gesorteerd op waarde (hoog naar laag)";
      case "all":
      default:
        return "Alle klanten";
    }
  };

  const handleMobileFilterChange = (filter) => {
    setMobileFilter(filter);
    setDisplayCount(20);
    const searchResults = searchText ? utils.wildCardSearchOnCustomer(customerList, searchText) : customerList;
    const filtered = applyMobileFilter(searchResults, filter);
    setFilteredList(filtered);
  };

  const CustomerCard = ({ user }) => {
    const cart = carts?.find((cart) => cart.id === user.id);
    const cartItemCount = cart?.products?.length || 0;
    const cartTotal = cart?.products?.reduce((sum, product) => sum + product.price * product.quantity, 0) || 0;

    return (
      <MobileCustomerCard onClick={() => showDrawerUser(user)} hoverable>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>{user?.company?.name}</h4>
            <div style={{ fontSize: 12, color: "#999" }}>
              {user.lastLoginTimestamp && (
                <div>
                  {t("last_login_timestamp")}: {dayjs.unix(user.lastLoginTimestamp).fromNow()}
                </div>
              )}
              <div>
                {t("last_order")}: {user.lastOrderTimestamp ? dayjs.unix(user.lastOrderTimestamp).fromNow() : "-"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <Tag color={user.isSalesPerson ? "lime" : "pink"}>
              {user.isSalesPerson ? t("sales_person") : t("client")}
            </Tag>
            {cartItemCount > 0 && (
              <Tag color={"green"}>
                <span style={{ fontWeight: 600 }}>
                  {CURRENCY}
                  {formatPrice(cartTotal)}
                </span>
              </Tag>
            )}
          </div>
        </div>
      </MobileCustomerCard>
    );
  };

  return (
    <>
      {contextHolder}
      {isMobile ? (
        <>
          <MobileFilterBar>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <FilterDescription>{getFilterDescription()}</FilterDescription>
          </MobileFilterBar>
          <div style={{ marginTop: 8, marginInline: 6, paddingBottom: 80 }}>
            {filteredList && filteredList.length > 0 ? (
              <>
                <Space direction="vertical" size={12} style={{ width: "100%", gap: 0 }}>
                  {filteredList.slice(0, displayCount).map((user) => (
                    <CustomerCard key={user.email} user={user} />
                  ))}
                </Space>
                {displayCount < filteredList.length && (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Button
                      type="default"
                      onClick={() => setDisplayCount((prev) => Math.min(prev + 20, filteredList.length))}
                      style={{ minWidth: 200 }}
                    >
                      Laad meer ({filteredList.length - displayCount} resterend)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_data")}</div>
            )}
          </div>
          <BottomNavigation>
            <FilterButton active={mobileFilter === "all"} onClick={() => handleMobileFilterChange("all")}>
              <TeamOutlined style={{ fontSize: 20 }} />
              <span>Alle</span>
            </FilterButton>
            <FilterButton
              active={mobileFilter === "activelyOrdering"}
              onClick={() => handleMobileFilterChange("activelyOrdering")}
            >
              <ShoppingOutlined style={{ fontSize: 20 }} />
              <span>Bezig</span>
            </FilterButton>
            <FilterButton
              active={mobileFilter === "missedOpportunity"}
              onClick={() => handleMobileFilterChange("missedOpportunity")}
            >
              <ClockCircleOutlined style={{ fontSize: 20 }} />
              <span>Gemist</span>
            </FilterButton>
            <FilterButton active={mobileFilter === "cartValue"} onClick={() => handleMobileFilterChange("cartValue")}>
              <DollarOutlined style={{ fontSize: 20 }} />
              <span>Waarde</span>
            </FilterButton>
          </BottomNavigation>
        </>
      ) : (
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div className="mr-md-3 " style={{ gap: 6, display: "flex" }}>
                <Input
                  value={searchText}
                  placeholder={t("search")}
                  prefix={<SearchOutlined />}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
              <DesktopFilterButtons>
                <DesktopFilterButton active={mobileFilter === "all"} onClick={() => handleMobileFilterChange("all")}>
                  <TeamOutlined />
                  <span>Alle</span>
                </DesktopFilterButton>
                <DesktopFilterButton
                  active={mobileFilter === "activelyOrdering"}
                  onClick={() => handleMobileFilterChange("activelyOrdering")}
                >
                  <ShoppingOutlined />
                  <span>Bezig</span>
                </DesktopFilterButton>
                <DesktopFilterButton
                  active={mobileFilter === "missedOpportunity"}
                  onClick={() => handleMobileFilterChange("missedOpportunity")}
                >
                  <ClockCircleOutlined />
                  <span>Gemist</span>
                </DesktopFilterButton>
                <DesktopFilterButton
                  active={mobileFilter === "cartValue"}
                  onClick={() => handleMobileFilterChange("cartValue")}
                >
                  <DollarOutlined />
                  <span>Waarde</span>
                </DesktopFilterButton>
              </DesktopFilterButtons>
            </div>
            <Dropdown overlay={exportMenu}>
              <Button className="ant-btn-primary">
                <FileExcelOutlined /> {t("export")} <DownOutlined />
              </Button>
            </Dropdown>
          </div>
          {mobileFilter !== "all" && (
            <FilterDescription style={{ marginBottom: 16 }}>{getFilterDescription()}</FilterDescription>
          )}
          {scope.type === SCOPE_COUNTRIES && (
            <FilterDescription style={{ marginBottom: 16 }}>
              {t("access.scoped_hint", { countries: scope.countries.join(", ") })}
            </FilterDescription>
          )}
          {scope.type === SCOPE_CUSTOMERS && (
            <FilterDescription style={{ marginBottom: 16 }}>
              {t("access.scoped_hint_customers", { count: scope.customerIds.length })}
            </FilterDescription>
          )}
          {scope.type === SCOPE_NONE && (
            <FilterDescription style={{ marginBottom: 16 }}>{t("access.scoped_hint_none")}</FilterDescription>
          )}
          <div className="table-responsive">
            <Table columns={tableColumns} dataSource={filteredList} rowKey="email" scroll={{ x: 1300 }} />
          </div>
        </Card>
      )}
      {editModalVisible && (
        <Modal
          title={t("edit_user", { name: editUser.company.name })}
          open={editModalVisible}
          onCancel={closeEditModal}
          onOk={saveUser}
          okText={t("save")}
          cancelText={t("close")}
          width={1000}
          confirmLoading={submitLoading}
        >
          <Form form={editForm} layout="vertical">
            <InputLabel>{t("is_sales_person")}</InputLabel>
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              checked={isSalesPerson}
              onChange={setIsSalesPerson}
            />
            {!isSalesPerson && (
              <>
                <InputLabel className={"mt-3"}>{t("sales_person")}</InputLabel>
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={true}
                  className="w-100"
                  onChange={setSelectedSalesPerson}
                  value={selectedSalesPerson}
                >
                  <Option key={0} value={null} className={"w-100"}>
                    {t("none")}
                  </Option>
                  {salesPersonUsers.map((user) => {
                    let name = user.email;
                    if (user.firstName && user.lastName) name = `${user.firstName} ${user.lastName}`;
                    if (user.company?.name) name += `- ${user.company.name}`;
                    return (
                      <Option key={user.id} value={user.id} className={"w-100"}>
                        {name}
                      </Option>
                    );
                  })}
                </Select>
              </>
            )}
            <Divider />
            <InputLabel>{t("marketing_materials")}</InputLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              <Checkbox checked={hasCatalog} onChange={(e) => setHasCatalog(e.target.checked)}>
                {t("catalog")}
              </Checkbox>
              <Checkbox checked={hasPromotionalMaterial} onChange={(e) => setHasPromotionalMaterial(e.target.checked)}>
                {t("promotional_material")}
              </Checkbox>
            </div>
            <Divider />
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="firstName"
                  label={t("first_name")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("first_name")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="lastName"
                  label={t("last_name")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("last_name")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="companyName"
                  label={t("company_name")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("company_name")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vatNumber"
                  label={t("vat_number")}
                  rules={[
                    () => ({
                      validator(rule, value) {
                        return validateVatNumber(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              isCountryInEuropeanUnion(countryCode)
                                ? t("form.enter_valid_vat_number")
                                : t("form.enter_valid_or_empty_vat_number"),
                            );
                      },
                    }),
                  ]}
                >
                  <Input
                    onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                    placeholder={t("vat_number")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="country"
                  label={t("country")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder={t("country")}
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    onChange={setCountryCode}
                  >
                    {countryData.map((country, index) => {
                      return (
                        <Option value={country.value} key={index}>
                          {country.label}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={6}>
                <Form.Item
                  name="zipCode"
                  label={t("zip_code")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("zip_code")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={5}>
                <Form.Item
                  name="houseNumber"
                  label={t("house_number")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("house_number")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={5}>
                <Form.Item name="addition" label={t("house_number_addition")}>
                  <Input placeholder={t("house_number_addition")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="street"
                  label={t("street")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("street")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="city"
                  label={t("city")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("city")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="phone"
                  label={t("phone")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_value"),
                    },
                  ]}
                >
                  <Input placeholder={t("phone")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="email"
                  label={t("email")}
                  disabled
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_email"),
                    },
                    {
                      type: "email",
                      message: t("form.enter_valid_email"),
                    },
                  ]}
                >
                  <Input placeholder={t("email")} />
                </Form.Item>
              </Col>
            </Row>
            <Checkbox checked={!invoiceDiffers} onChange={() => setInvoiceDiffers(!invoiceDiffers)}>
              {t("invoice_address_match_delivery")}
            </Checkbox>
            {invoiceDiffers && (
              <div style={{ marginTop: "1rem" }}>
                <h4>{t("invoice_details")}</h4>
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={10}>
                    <Form.Item
                      name="invoiceStreet"
                      label={t("street")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_value"),
                        },
                      ]}
                    >
                      <Input placeholder={t("street")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="invoiceHouseNumber"
                      label={t("house_number")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_value"),
                        },
                      ]}
                    >
                      <Input placeholder={t("house_number")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item name="invoiceAddition" label={t("house_number_addition")}>
                      <Input placeholder={t("house_number_addition")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="invoiceZipCode"
                      label={t("zip_code")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_value"),
                        },
                      ]}
                    >
                      <Input placeholder={t("zip_code")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="invoiceCity"
                      label={t("city")}
                      rules={[
                        {
                          required: true,
                          message: t("form.enter_value"),
                        },
                      ]}
                    >
                      <Input placeholder={t("city")} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}
          </Form>
        </Modal>
      )}
      <CustomerDrawer
        user={drawerUser}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setDrawerUser(null);
        }}
        onOrderClick={showOrderDrawer}
      />
      <OrderDrawer order={currentOrder} open={!!currentOrder} closeDrawer={() => setCurrentOrder(null)} />
    </>
  );
};

const InputLabel = styled.p`
  color: #333;
  margin-bottom: 0;
`;

const MobileCustomerCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin-bottom: 12px;

  &:active {
    transform: scale(0.98);
  }

  .ant-card-body {
    padding: 12px;
  }
`;

const MobileFilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const FilterDescription = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid ${colors.cta};
`;

const CartValueBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, ${colors.cta} 0%, #c85a00 100%);
  color: #fff;
  border-radius: 6px;
  font-size: 15px;
  box-shadow: 0 2px 4px rgba(167, 76, 0, 0.2);
`;

const BottomNavigation = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-around;
  padding: 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 100;
`;

const FilterButton = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props) => (props.active ? colors.cta : "#666")};
  font-size: 12px;
  font-weight: ${(props) => (props.active ? "600" : "400")};

  &:active {
    transform: scale(0.95);
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

const DesktopFilterButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DesktopFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.active ? colors.cta : "#d9d9d9")};
  background: ${(props) => (props.active ? colors.cta : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#666")};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};

  &:hover {
    border-color: ${colors.cta};
    color: ${(props) => (props.active ? "#fff" : colors.cta)};
  }

  &:active {
    transform: scale(0.98);
  }

  span {
    white-space: nowrap;
  }
`;

const CartItemCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
`;

const CartSummary = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
  border-radius: 8px;
  border: 2px solid ${colors.cta};
  box-shadow: 0 2px 8px rgba(167, 76, 0, 0.1);
`;

const OrderCard = styled.div`
  padding: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.cta};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default Customers;
